<script setup lang="ts">
import { computed } from 'vue';

const { collapsed, progress, progressLabel } = defineProps<{
	collapsed: boolean;
	progress: number;
	progressLabel: string;
}>();

const emit = defineEmits<{
	collapse: [];
	dismiss: [];
}>();

const PROGRESS_PATH_LENGTH = 100;
const progressOffset = computed(() => PROGRESS_PATH_LENGTH - progress * PROGRESS_PATH_LENGTH);
</script>

<template>
	<header
		class="
			flex items-center
			motion-safe:transition-[padding] motion-safe:duration-(--onboarding-collapse-duration) motion-safe:ease-out
			sm:transition-none
		"
		:class="collapsed ? 'gap-3 px-3 sm:gap-2.5 sm:px-2' : 'gap-3 px-3 pb-2 sm:gap-2 sm:px-2'"
	>
		<div
			data-test="onboarding-progress"
			class="
				relative shrink-0
				motion-safe:transition-[width,height] motion-safe:duration-(--onboarding-transition-duration)
				motion-safe:ease-in-out
			"
			:class="collapsed ? 'size-6 sm:size-5' : 'size-4 sm:size-3'"
		>
			<svg viewBox="0 0 36 36" class="size-full -rotate-90" aria-hidden="true">
				<circle
					cx="18"
					cy="18"
					r="15"
					:pathLength="PROGRESS_PATH_LENGTH"
					fill="none"
					class="stroke-(--ui-border-accented)"
					:stroke-width="collapsed ? 3 : 5"
				/>
				<circle
					cx="18"
					cy="18"
					r="15"
					:pathLength="PROGRESS_PATH_LENGTH"
					fill="none"
					class="stroke-primary transition-[stroke-dashoffset,stroke-width] duration-500 ease-out"
					:stroke-width="collapsed ? 3 : 5"
					stroke-linecap="round"
					:stroke-dasharray="PROGRESS_PATH_LENGTH"
					:stroke-dashoffset="progressOffset"
				/>
			</svg>
		</div>
		<div class="min-w-0 grow">
			<h2
				class="truncate font-semibold text-highlighted"
				:class="collapsed ? 'text-base sm:text-sm' : 'text-base sm:text-xs'"
			>
				{{ $t('onboarding_popup.heading') }}
			</h2>
			<p class="truncate text-muted" :class="collapsed ? 'text-sm sm:text-xs' : 'text-sm sm:text-[11px]/4'">
				{{ progressLabel }}
			</p>
		</div>
		<div class="ms-auto grid shrink-0">
			<Transition
				enter-active-class="motion-safe:transition-[opacity,filter] motion-safe:duration-(--onboarding-transition-duration) ease-out"
				enter-from-class="motion-safe:opacity-0 motion-safe:blur-[2px]"
				enter-to-class="opacity-100 blur-none"
				leave-active-class="motion-safe:transition-[opacity,filter] motion-safe:duration-(--onboarding-transition-duration) ease-out"
				leave-from-class="opacity-100 blur-none"
				leave-to-class="motion-safe:opacity-0 motion-safe:blur-[2px]"
			>
				<div
					v-if="collapsed"
					key="collapsed"
					data-test="onboarding-reopen-control"
					class="col-start-1 row-start-1 grid size-9 place-items-center sm:size-6"
				>
					<DIcon name="lucide:chevron-up" class="size-5 text-dimmed sm:size-4" />
				</div>
				<div v-else key="expanded" class="col-start-1 row-start-1 flex items-center gap-x-1">
					<DTooltip :text="$t('onboarding_popup.minimize')">
						<DButton
							icon="lucide:minus"
							color="neutral"
							variant="ghost"
							size="xs"
							data-test="onboarding-minimize"
							:aria-label="$t('onboarding_popup.minimize')"
							class="p-2 sm:p-1"
							:ui="{ leadingIcon: 'size-5 sm:size-4' }"
							@click="emit('collapse')"
						/>
					</DTooltip>
					<DTooltip :text="$t('onboarding_popup.dismiss')">
						<DButton
							icon="lucide:x"
							color="neutral"
							variant="ghost"
							size="xs"
							data-test="onboarding-dismiss"
							:aria-label="$t('onboarding_popup.dismiss')"
							class="p-2 sm:p-1"
							:ui="{ leadingIcon: 'size-5 sm:size-4' }"
							@click="emit('dismiss')"
						/>
					</DTooltip>
				</div>
			</Transition>
		</div>
	</header>
</template>
